import { useQuery, useQueryClient } from '@tanstack/react-query';
import { D, parseMoney, priceExcludingTax, priceIncludingTax } from '@makila/fiscal';
import { ArrowDownLeft, ArrowUpRight, MoreHorizontal, Package, PackagePlus, Pencil, Scale, Trash2 } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { SupplierPicker, type PickSupplier, useCategories, useReference, useWarehouses } from '../components/pickers';
import {
  Alert, Badge, Button, Card, Chips, Dropdown, Empty, errorText, Field, fieldErrors, Input, KeyValues, Loading, Modal, PageHeader, Pagination,
  SearchInput, Select, StatCard, SuggestInput, Switch, Table, Tabs, Td, Textarea, Th, Toolbar, Tr, useConfirm, useToast,
} from '../components/ui';
import { UNITS } from '../lib/lists';
import { del, get, patch, post } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateTime, mt } from '../lib/format';
import { StockMovementModal } from './stock';

type P = Record<string, any>;
const canon = (v: unknown) => (v === null || v === undefined || v === '' ? '' : String(Number(v)));
type FormTab = 'geral' | 'precos' | 'stock';

export function ProductForm({ initial, onSaved, onCancel }: { initial?: P; onSaved: (p: P) => void; onCancel: () => void }) {
  const toast = useToast();
  const ref = useReference();
  const cats = useCategories();
  const { has } = useAuth();
  const whs = useWarehouses();
  const [tab, setTab] = useState<FormTab>('geral');
  const [supplier, setSupplier] = useState<PickSupplier | null>(initial?.supplier_id ? { id: initial.supplier_id, name: initial.supplier_name ?? 'Fornecedor', code: '', nuit: null, city: '', payment_terms_days: null, default_payment_method: null } : null);
  const [f, setF] = useState({
    code: initial?.code ?? '', name: initial?.name ?? '', description: initial?.description ?? '', kind: initial?.kind ?? 'S', unit: initial?.unit ?? 'un',
    barcode: initial?.barcode ?? '', categoryId: initial?.category_id ?? '', unitPrice: canon(initial?.unit_price), priceIncludesTax: !!initial?.price_includes_tax,
    taxRate: initial ? Number(initial.tax_rate) : 16, exemptionCode: initial?.exemption_code ?? '', costPrice: canon(initial?.cost_price ?? 0), price2: canon(initial?.price2),
    supplierRef: initial?.supplier_ref ?? '', trackStock: !!initial?.track_stock, minStock: canon(initial?.min_stock), maxStock: canon(initial?.max_stock),
    defaultWarehouseId: initial?.default_warehouse_id ?? '', active: initial?.active ?? true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  // Margem sobre o custo, com o preço convertido para base tributável.
  const margin = useMemo(() => {
    try {
      const net = new D(priceExcludingTax(parseMoney(f.unitPrice) ?? '0', f.taxRate, f.priceIncludesTax));
      const cost = new D(parseMoney(f.costPrice) ?? '0');
      if (net.isZero()) return null;
      return { value: net.minus(cost), pct: net.minus(cost).div(net).times(100) };
    } catch { return null; }
  }, [f.unitPrice, f.costPrice, f.taxRate, f.priceIncludesTax]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErrors({});
    const body = {
      ...f, description: f.description || null, barcode: f.barcode || null, categoryId: f.categoryId || null, exemptionCode: f.taxRate === 0 ? f.exemptionCode || null : null,
      price2: f.price2 || null, supplierId: supplier?.id ?? null, supplierRef: f.supplierRef || null, minStock: f.minStock || null, maxStock: f.maxStock || null,
      defaultWarehouseId: f.defaultWarehouseId || null, kind: f.kind as 'P', trackStock: f.kind === 'P' ? f.trackStock : false,
    };
    try {
      const r = initial ? await patch(`/products/${initial.id}`, body) : await post('/products', body);
      toast(initial ? 'Artigo actualizado' : 'Artigo criado');
      onSaved(r.product);
    } catch (ex) {
      const fe = fieldErrors(ex);
      setErrors(fe);
      if (fe.unitPrice || fe.exemptionCode || fe.costPrice) setTab('precos'); else if (fe.code || fe.name) setTab('geral');
      toast(errorText(ex), 'error');
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Tabs value={tab} onChange={setTab} className="-mx-6 -mt-5 mb-5 px-6" tabs={[{ id: 'geral', label: 'Geral' }, { id: 'precos', label: 'Preços e IVA' }, { id: 'stock', label: has('stock') ? 'Stock e fornecedor' : 'Fornecedor', hidden: f.kind !== 'P' || (!has('stock') && !has('purchases')) }]} />
      <div className="min-h-[310px]">
        {tab === 'geral' && (
          <div className="space-y-4">
            <Chips value={f.kind} onChange={(v) => setF({ ...f, kind: v })} options={[{ id: 'P', label: 'Produto' }, { id: 'S', label: 'Serviço' }, { id: 'O', label: 'Outro' }]} />
            <div className="grid gap-4 sm:grid-cols-4">
              <Field label="Código" required error={errors.code}><Input autoFocus value={f.code} onChange={set('code')} /></Field>
              <Field label="Designação" required className="sm:col-span-3" error={errors.name} hint="É o texto que sai nos documentos"><Input value={f.name} onChange={set('name')} /></Field>
            </div>
            <Field label="Descrição detalhada" hint="Opcional — impressa por baixo da designação"><Textarea rows={3} value={f.description} onChange={set('description')} /></Field>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Categoria">
                <Select value={f.categoryId} onChange={set('categoryId')}><option value="">Sem categoria</option>{cats.data?.items.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
              </Field>
              <Field label="Unidade">
                <SuggestInput suggestions={UNITS} maxLength={15} value={f.unit} onChange={set('unit')} />
              </Field>
              <Field label="Código de barras"><Input value={f.barcode} onChange={set('barcode')} placeholder="EAN-13" /></Field>
            </div>
            {initial && <Switch label="Artigo activo" hint="Inactivos não aparecem na pesquisa ao facturar" checked={f.active} onChange={(v) => setF({ ...f, active: v })} />}
          </div>
        )}
        {tab === 'precos' && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Preço de venda" required error={errors.unitPrice}><Input inputMode="decimal" value={f.unitPrice} onChange={set('unitPrice')} trailing="MT" /></Field>
              <Field label="Taxa de IVA">
                <Select value={f.taxRate} onChange={(e) => setF({ ...f, taxRate: Number(e.target.value) })}><option value={16}>16% — normal</option><option value={5}>5% — reduzida</option><option value={0}>Isento</option></Select>
              </Field>
              <Field label="2.º preço (revenda)" hint="Opcional"><Input inputMode="decimal" value={f.price2} onChange={set('price2')} trailing="MT" /></Field>
            </div>
            <Switch label="O preço já inclui IVA" hint="Para retalho: o cliente paga exactamente este valor (CIVA art. 30)" checked={f.priceIncludesTax} onChange={(v) => setF({ ...f, priceIncludesTax: v })} />
            {f.taxRate === 0 && (
              <Field label="Motivo de isenção" required error={errors.exemptionCode} hint="Obrigatório: é impresso no documento (CIVA art. 27 n.º 5 e)">
                <Select value={f.exemptionCode} onChange={set('exemptionCode')}>
                  <option value="">Escolha o motivo…</option>
                  {ref.data?.exemptions.map((x) => <option key={x.code} value={x.code}>{x.code} — {x.description} ({x.legal_basis})</option>)}
                  {ref.data?.expiredExemptions?.filter((x) => x.code === f.exemptionCode).map((x) => <option key={x.code} value={x.code} disabled>{x.code} — {x.description} (caducou em {x.valid_to?.split('-').reverse().join('/')} — escolha outro)</option>)}
                </Select>
              </Field>
            )}
            <div className="grid gap-4 rounded-xl border border-[var(--border)] p-4 sm:grid-cols-3">
              <Field label="Preço de custo" hint="Referência para margem"><Input inputMode="decimal" value={f.costPrice} onChange={set('costPrice')} trailing="MT" /></Field>
              <div><div className="text-[13px] font-medium text-[var(--text-2)]">Preço s/ IVA · c/ IVA</div>
                <div className="mt-2 text-sm tabular-nums">{parseMoney(f.unitPrice) ? `${mt(priceExcludingTax(parseMoney(f.unitPrice)!, f.taxRate, f.priceIncludesTax))} · ${mt(priceIncludingTax(parseMoney(f.unitPrice)!, f.taxRate, f.priceIncludesTax))}` : '—'}</div></div>
              <div><div className="text-[13px] font-medium text-[var(--text-2)]">Margem bruta</div>
                <div className={`mt-2 text-sm font-semibold tabular-nums ${margin && margin.value.isNegative() ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{margin ? `${mt(margin.value.toFixed(2))} MT (${margin.pct.toFixed(1)}%)` : '—'}</div></div>
            </div>
          </div>
        )}
        {tab === 'stock' && (
          <div className="space-y-4">
            {has('stock') && <Switch label="Controlar stock deste artigo" hint="As vendas dão saída automática e as compras dão entrada" checked={f.trackStock} onChange={(v) => setF({ ...f, trackStock: v })} />}
            {has('stock') && f.trackStock && (
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Stock mínimo" hint="Alerta abaixo deste valor"><Input inputMode="decimal" value={f.minStock} onChange={set('minStock')} /></Field>
                <Field label="Stock máximo"><Input inputMode="decimal" value={f.maxStock} onChange={set('maxStock')} /></Field>
                <Field label="Armazém por omissão">
                  <Select value={f.defaultWarehouseId} onChange={set('defaultWarehouseId')}><option value="">O principal</option>{whs.data?.items.filter((w) => w.active || w.id === f.defaultWarehouseId).map((w) => <option key={w.id} value={w.id}>{w.name}{w.active ? '' : ' (inactivo)'}</option>)}</Select>
                </Field>
              </div>
            )}
            {has('purchases') && <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-2"><div className="text-[13px] font-medium text-[var(--text-2)]">Fornecedor habitual</div><SupplierPicker value={supplier} onChange={setSupplier} /></div>
              <Field label="Referência do fornecedor"><Input value={f.supplierRef} onChange={set('supplierRef')} /></Field>
            </div>}
          </div>
        )}
      </div>
      <div className="-mx-6 mt-5 -mb-5 flex justify-end gap-2 border-t border-[var(--border)] bg-[var(--surface-2)] px-6 py-3.5">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" loading={busy}>{initial ? 'Guardar alterações' : 'Criar artigo'}</Button>
      </div>
    </form>
  );
}

export function ProductsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const cats = useCategories();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<'all' | 'P' | 'S' | 'low' | 'inactive'>('all');
  const [cat, setCat] = useState('');
  const creating = sp.get('novo') === '1';
  const setCreating = (v: boolean) => { const p = new URLSearchParams(sp); if (v) p.set('novo', '1'); else p.delete('novo'); setSp(p, { replace: true }); };
  const params = new URLSearchParams({ page: String(page), pageSize: '25', q });
  if (kind === 'P' || kind === 'S') params.set('kind', kind);
  if (kind === 'low') params.set('stock', 'low');
  if (kind === 'inactive') params.set('active', 'inactive');
  if (cat) params.set('categoryId', cat);
  const list = useQuery({ queryKey: ['products', params.toString()], queryFn: () => get<{ items: P[]; total: number }>(`/products?${params}`) });

  return (
    <>
      <PageHeader eyebrow="Stock" title="Artigos" subtitle="Produtos e serviços que vende"
        actions={can('products:create') && <Button icon={<PackagePlus className="size-4" />} onClick={() => setCreating(true)}>Novo artigo</Button>} />
      <Card padded={false}>
        <Toolbar>
          <SearchInput className="w-64" value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Código, designação ou código de barras" />
          <Chips value={kind} onChange={(v) => { setKind(v); setPage(1); }} options={[{ id: 'all', label: 'Todos' }, { id: 'P', label: 'Produtos' }, { id: 'S', label: 'Serviços' }, { id: 'low', label: 'Stock baixo' }, { id: 'inactive', label: 'Inactivos' }]} />
          <Select className="ml-auto h-8 w-48 text-xs" value={cat} onChange={(e) => { setCat(e.target.value); setPage(1); }} aria-label="Categoria">
            <option value="">Todas as categorias</option>{cats.data?.items.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Toolbar>
        {list.isLoading ? <div className="p-5"><Loading /></div> : !list.data?.items.length ? (
          <Empty icon={<Package />} title="Sem artigos" action={can('products:create') && !q && <Button icon={<PackagePlus className="size-4" />} onClick={() => setCreating(true)}>Criar artigo</Button>}>Crie os produtos e serviços que vende para facturar mais depressa.</Empty>
        ) : (
          <>
            <Table>
              <thead><tr><Th>Artigo</Th><Th>Categoria</Th><Th>IVA</Th><Th className="text-right">Preço s/ IVA</Th><Th className="text-right">Preço c/ IVA</Th><Th className="text-right">Custo médio</Th><Th className="text-right">Stock</Th></tr></thead>
              <tbody>
                {list.data.items.map((p) => {
                  const low = p.track_stock && p.min_stock != null && Number(p.stock) < Number(p.min_stock);
                  return (
                    <Tr key={p.id} onClick={() => nav(`/artigos/${p.id}`)}>
                      <Td><div className="font-medium">{p.name} {!p.active && <Badge tone="red">Inactivo</Badge>}</div><div className="text-xs text-[var(--muted)]">{p.code} · {p.kind === 'P' ? 'Produto' : p.kind === 'S' ? 'Serviço' : 'Outro'} · {p.unit}</div></Td>
                      <Td className="text-[var(--text-2)]">{p.category_name ?? '—'}</Td>
                      <Td>{Number(p.tax_rate) ? `${Number(p.tax_rate)}%` : <Badge>Isento</Badge>}</Td>
                      <Td className="num">{mt(priceExcludingTax(p.unit_price, p.tax_rate, p.price_includes_tax))}</Td>
                      <Td className="num font-medium">{mt(priceIncludingTax(p.unit_price, p.tax_rate, p.price_includes_tax))}</Td>
                      <Td className="num text-[var(--muted)]">{p.track_stock ? mt(p.avg_cost) : '—'}</Td>
                      <Td className="num">{p.track_stock ? <span className={low ? 'font-semibold text-red-600' : ''}>{Number(p.stock)}{low && ' ⚠'}</span> : '—'}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      <Modal open={creating} onClose={() => setCreating(false)} title="Novo artigo" size="lg" icon={<PackagePlus />}>
        <ProductForm onCancel={() => setCreating(false)} onSaved={(p) => { setCreating(false); qc.invalidateQueries({ queryKey: ['products'] }); nav(`/artigos/${p.id}`); }} />
      </Modal>
    </>
  );
}

export function ProductPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);
  const q = useQuery({ queryKey: ['product', id], queryFn: () => get(`/products/${id}`) });
  if (q.isLoading || !q.data) return <Loading />;
  const { product: p, byWarehouse, movements, sales } = q.data;
  const total = byWarehouse.reduce((s: number, w: any) => s + Number(w.stock), 0);
  const low = p.track_stock && p.min_stock != null && total < Number(p.min_stock);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['product', id] }); qc.invalidateQueries({ queryKey: ['products'] }); qc.invalidateQueries({ queryKey: ['nav-counts'] }); };
  const remove = async () => {
    if (!(await confirm({ title: `Remover ${p.name}?`, description: 'Se o artigo já foi usado em documentos, fica apenas inactivo.', tone: 'danger', confirmLabel: 'Remover' }))) return;
    try { const r = await del(`/products/${id}`); toast(r.deactivated ? 'Artigo desactivado' : 'Artigo removido'); refresh(); nav('/artigos'); } catch (e) { toast(errorText(e), 'error'); }
  };

  return (
    <>
      <PageHeader eyebrow={<Link to="/artigos" className="hover:underline">Artigos</Link>} title={<span className="flex items-center gap-3">{p.name} {!p.active && <Badge tone="red">Inactivo</Badge>}</span>}
        subtitle={`${p.code} · ${p.kind === 'P' ? 'Produto' : p.kind === 'S' ? 'Serviço' : 'Outro'} · ${p.unit}`}
        actions={<>
          {p.track_stock && can('stock:create') && <Button icon={<Scale className="size-4" />} onClick={() => setMoving(true)}>Movimentar stock</Button>}
          {can('products:edit') && <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>Editar</Button>}
          <Dropdown trigger={<Button variant="secondary" iconOnly icon={<MoreHorizontal className="size-4" />} aria-label="Mais acções" />} items={[
            { label: 'Remover artigo', icon: <Trash2 />, danger: true, onClick: remove, hidden: !can('products:delete') },
          ]} />
        </>} />
      {low && <div className="mb-5"><Alert tone="warn" title="Stock abaixo do mínimo">Existem {total} {p.unit}, abaixo do mínimo de {Number(p.min_stock)}.</Alert></div>}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Preço c/ IVA" value={`${mt(priceIncludingTax(p.unit_price, p.tax_rate, p.price_includes_tax))} MT`} sub={`${Number(p.tax_rate) ? `IVA ${Number(p.tax_rate)}%` : 'Isento'} · s/ IVA ${mt(priceExcludingTax(p.unit_price, p.tax_rate, p.price_includes_tax))}`} />
        <StatCard label="Stock total" value={p.track_stock ? `${total} ${p.unit}` : 'Não controla'} tone={low ? 'warn' : undefined} sub={p.track_stock ? `Mín. ${p.min_stock != null ? Number(p.min_stock) : '—'} · Máx. ${p.max_stock != null ? Number(p.max_stock) : '—'}` : undefined} />
        <StatCard label="Custo médio" value={`${mt(p.avg_cost)} MT`} sub={p.track_stock ? `Valor em stock ${mt(total * Number(p.avg_cost))} MT` : `Custo de referência ${mt(p.cost_price)}`} />
        <StatCard label="Vendido este ano" value={`${Number(sales.qty)} ${p.unit}`} sub={`${mt(sales.net)} MT s/ IVA`} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card title="Detalhes" className="lg:col-span-1">
          <KeyValues cols={1} items={[
            ['Descrição', p.description], ['Código de barras', p.barcode], ['2.º preço', p.price2 && `${mt(p.price2)} MT`],
            ['Isenção', p.exemption_code], ['Referência do fornecedor', p.supplier_ref],
          ]} />
          {p.track_stock && (
            <div className="mt-5">
              <div className="mb-2 text-xs font-semibold tracking-wider text-[var(--muted)] uppercase">Por armazém</div>
              <ul className="divide-y divide-[var(--border)] text-sm">
                {byWarehouse.map((w: any) => <li key={w.id} className="flex justify-between py-2"><span>{w.name}</span><span className="tabular-nums font-medium">{Number(w.stock)}</span></li>)}
              </ul>
            </div>
          )}
        </Card>
        <Card title="Últimos movimentos de stock" className="lg:col-span-2" padded={false} actions={<Link to={`/stock/movimentos?artigo=${id}`}><Button size="sm" variant="ghost">Ver todos</Button></Link>}>
          {!movements.length ? <Empty title="Sem movimentos" /> : (
            <Table>
              <thead><tr><Th>Data</Th><Th>Movimento</Th><Th>Armazém</Th><Th className="text-right">Qtd.</Th><Th className="text-right">Custo</Th></tr></thead>
              <tbody>{movements.map((m: any) => (
                <Tr key={m.id} onClick={m.document_id ? () => nav(`/documentos/${m.document_id}`) : m.purchase_id ? () => nav(`/compras/${m.purchase_id}`) : undefined}>
                  <Td className="whitespace-nowrap">{dateTime(m.created_at)}</Td>
                  <Td><span className="flex items-center gap-2">{m.kind === 'in' ? <ArrowDownLeft className="size-4 text-emerald-600" /> : m.kind === 'out' ? <ArrowUpRight className="size-4 text-red-500" /> : <Scale className="size-4 text-blue-500" />}{m.note}</span></Td>
                  <Td>{m.warehouse}</Td>
                  <Td className={`num font-medium ${m.kind === 'out' || Number(m.quantity) < 0 ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{m.kind === 'out' ? '−' : Number(m.quantity) > 0 ? '+' : ''}{Number(m.quantity)}</Td>
                  <Td className="num text-[var(--muted)]">{m.unit_cost ? mt(m.unit_cost) : '—'}</Td>
                </Tr>
              ))}</tbody>
            </Table>
          )}
        </Card>
      </div>
      <Modal open={editing} onClose={() => setEditing(false)} title="Editar artigo" size="lg" icon={<Pencil />}>
        <ProductForm initial={p} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />
      </Modal>
      <StockMovementModal open={moving} onClose={() => setMoving(false)} product={p} onSaved={refresh} />
    </>
  );
}

